import { useState } from "react";
import { useNavigate, useSearchParams } from "@/lib/router-compat";
import { Loader2 } from "lucide-react";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import { trackLeadConversion } from "@/lib/conversions";

interface BcbsilOptinFormProps {
  employerName?: string | null;
}

export function BcbsilOptinForm({ employerName }: BcbsilOptinFormProps = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [whoIsThisFor, setWhoIsThisFor] = useState("Me (the employee)");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hidden field values from query params or defaults
  const quizResult = searchParams.get("quiz_result") || "";
  const bcbsilRelationship = searchParams.get("bcbsil_relationship") || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim()) {
      setError("Please enter your first name.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!phone.trim()) {
      setError("Please enter your phone number.");
      return;
    }
    if (!consent) {
      setError("Please agree to the contact terms to request a consultation.");
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Post to unified CRM form tracking
      const trackingPayload = buildTrackingPayload(
        "fb-ads-bcbsil-saint",
        "SAINT BCBSIL Opt-in",
      );
      trackingPayload.formData = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
      };
      trackingPayload.formLabels = {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email",
        phone: "Phone",
      };

      postTrackingEvent(trackingPayload, {
        customFields: {
          MYQlMr3lrCpW6TonKqy8: {
            value: whoIsThisFor,
            label: "Who is this for?",
          },
          N4F73KEeils3e4crrgNS: {
            value: quizResult,
            label: "Quiz Result",
          },
          vPP16D6zMI7Sn45erJRk: {
            value:
              bcbsilRelationship ||
              (employerName ? `Employee at ${employerName}` : ""),
            label: "BCBSIL Relationship",
          },
          ...(employerName
            ? {
                Lo0LKbbbbzL5W37GsvVy: {
                  value: employerName,
                  label: "Employer Name",
                },
              }
            : {}),
        },
      });

      // 2. Site-wide conversion pixel (FB Pixel + GA4)
      trackLeadConversion("fb-ads-bcbsil-saint", "SAINT BCBSIL Opt-in");

      // 3. Push data layer event for GTM
      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "generate_lead",
          form_name: "SAINT BCBSIL Opt-in",
          form_id: "fb-ads-bcbsil-saint",
          form_category: "benefits_verification",
          thank_you_path: "/thank-you/saint-bcbsil",
        });
      }

      // Redirect to dedicated thank-you page
      navigate("/thank-you/saint-bcbsil");
    } catch (err) {
      console.error("Form submit error:", err);
      // Still redirect gracefully
      navigate("/thank-you/saint-bcbsil");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-left">
      <div>
        <h3 className="text-xl font-semibold text-primary tracking-tight">
          SAINT BCBSIL Opt-in
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Request your complimentary benefits check & Zoom consultation
        </p>
      </div>

      {error && (
        <div className="p-3 text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg">
          {error}
        </div>
      )}

      {/* Hidden Fields */}
      <input type="hidden" name="quiz_result" value={quizResult} />
      <input
        type="hidden"
        name="bcbsil_relationship"
        value={bcbsilRelationship}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Jane"
            className="w-full h-11 px-3.5 rounded-lg border border-border text-sm text-primary focus:outline-hidden focus:ring-2 focus:ring-accent focus:border-transparent bg-white"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            Last Name
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
            className="w-full h-11 px-3.5 rounded-lg border border-border text-sm text-primary focus:outline-hidden focus:ring-2 focus:ring-accent focus:border-transparent bg-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-primary mb-1">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jane.doe@example.com"
          className="w-full h-11 px-3.5 rounded-lg border border-border text-sm text-primary focus:outline-hidden focus:ring-2 focus:ring-accent focus:border-transparent bg-white"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-primary mb-1">
          Phone <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(555) 000-0000"
          className="w-full h-11 px-3.5 rounded-lg border border-border text-sm text-primary focus:outline-hidden focus:ring-2 focus:ring-accent focus:border-transparent bg-white"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-primary mb-1">
          Who is this for?
        </label>
        <select
          value={whoIsThisFor}
          onChange={(e) => setWhoIsThisFor(e.target.value)}
          className="w-full h-11 px-3.5 rounded-lg border border-border text-sm text-primary focus:outline-hidden focus:ring-2 focus:ring-accent focus:border-transparent bg-white"
        >
          <option value="Me (the employee)">Me (the employee)</option>
          <option value="A family member on my plan">
            A family member on my plan
          </option>
          <option value="Both">Both</option>
        </select>
      </div>

      {/* Consent Checkbox */}
      <div className="pt-1">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            required
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent"
          />
          <span className="text-xs text-muted-foreground leading-relaxed">
            I agree to be contacted by Neuro Wellness TMS Centers of America by
            phone, text, and email about my consultation. Consent isn&apos;t
            required to receive care. Message and data rates may apply.
          </span>
        </label>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-12 mt-2 px-6 rounded-xl font-semibold text-white bg-accent hover:bg-accent/90 transition-colors shadow-sm active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer disabled:opacity-75"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Submitting request...</span>
          </>
        ) : (
          <span>Request my free consultation</span>
        )}
      </button>

      <p className="text-[11px] text-center text-muted-foreground pt-1">
        Your information is kept strictly confidential. No spam.
      </p>
    </form>
  );
}
